import { useEffect, useRef, useState } from "react";
import Popper from "@mui/material/Popper";

const VIEWPORT_PADDING = 8;
const MENU_GAP = 4;
const menuSizing = {
  name: "catalogueMenuSizing",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn({ state }) {
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportHeight =
      viewport?.height ||
      document.documentElement.clientHeight ||
      window.innerHeight;
    const viewportWidth =
      viewport?.width ||
      document.documentElement.clientWidth ||
      window.innerWidth;
    const reference = state.elements.reference.getBoundingClientRect();
    const availableHeight = state.placement.startsWith("top")
      ? reference.top - viewportTop - VIEWPORT_PADDING - MENU_GAP
      : viewportTop +
        viewportHeight -
        reference.bottom -
        VIEWPORT_PADDING -
        MENU_GAP;
    state.styles.popper.width = `${Math.min(
      reference.width,
      viewportWidth - 2 * VIEWPORT_PADDING
    )}px`;
    state.elements.popper.style.setProperty(
      "--catalogue-item-menu-height",
      `${Math.max(0, Math.min(250, availableHeight - 2))}px`
    );
  },
};
const modifiers = [
  { name: "offset", options: { offset: [0, MENU_GAP] } },
  {
    name: "flip",
    options: { rootBoundary: "viewport", padding: VIEWPORT_PADDING },
  },
  {
    name: "preventOverflow",
    options: { rootBoundary: "viewport", padding: VIEWPORT_PADDING },
  },
  menuSizing,
];

export default function CatalogueItemDropdown({ anchorEl, onClose, children }) {
  const [menuEl, setMenuEl] = useState(null);
  const popperRef = useRef(null);

  useEffect(() => {
    if (!menuEl) return undefined;
    const isInside = (target) =>
      anchorEl?.contains(target) || menuEl.contains(target);
    const closeOutside = (event) => {
      if (!isInside(event.target)) onClose();
    };
    const handleKeyDown = (event) => {
      if (!isInside(event.target)) return;
      if (event.key === "Escape" || event.key === "Tab") {
        if (menuEl.contains(event.target)) {
          anchorEl
            ?.querySelector('[role="combobox"]')
            ?.focus({ preventScroll: true });
        }
        if (event.key === "Escape") event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const options = Array.from(
        menuEl.querySelectorAll('[role="option"]:not(:disabled)')
      );
      if (!options.length) return;
      event.preventDefault();
      const current = options.indexOf(document.activeElement);
      const next =
        event.key === "ArrowDown"
          ? (current + 1) % options.length
          : (current <= 0 ? options.length : current) - 1;
      options[next].focus({ preventScroll: true });
      options[next].scrollIntoView?.({ block: "nearest" });
    };
    document.addEventListener("mousedown", closeOutside, true);
    document.addEventListener("pointerdown", closeOutside, true);
    document.addEventListener("focusin", closeOutside);
    document.addEventListener("keydown", handleKeyDown);
    const visibilityObserver =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(([entry]) => {
            if (!entry.isIntersecting) onClose();
          })
        : null;
    if (anchorEl) visibilityObserver?.observe(anchorEl);
    return () => {
      visibilityObserver?.disconnect();
      document.removeEventListener("mousedown", closeOutside, true);
      document.removeEventListener("pointerdown", closeOutside, true);
      document.removeEventListener("focusin", closeOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [anchorEl, menuEl, onClose]);

  useEffect(() => {
    const update = () => popperRef.current?.update();
    update();
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (anchorEl) observer?.observe(anchorEl);
    if (menuEl) observer?.observe(menuEl);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      observer?.disconnect();
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, [anchorEl, menuEl]);

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      popperRef={popperRef}
      popperOptions={{ strategy: "fixed" }}
      placement="bottom-start"
      container={() =>
        anchorEl?.closest(".quotation-editor-page, .invoice-flow-shell") ||
        document.body
      }
      modifiers={modifiers}
      className="catalogue-item-popper"
      sx={{
        zIndex: (theme) => theme.zIndex.modal - 1,
        "& .quotation-item-search-dropdown .quotation-item-search-results-list":
          {
            boxSizing: "border-box",
            maxHeight: "var(--catalogue-item-menu-height, 250px)",
          },
      }}
    >
      <div
        ref={setMenuEl}
        className="quotation-item-search-dropdown"
        style={{ position: "relative", inset: "auto" }}
      >
        {children}
      </div>
    </Popper>
  );
}
